import type { HttpClient } from '@wix/http-client';
import {
  GENERATE_FULL_SITE_MAP,
  legalPagesToFilterSet,
  SECTION_LABEL_MAPPING,
  SectionTypeToFilter,
} from './aiContentProviderConsts';
import {
  AiPageMetadata,
  AiSectionMetadata,
  SectionMappingRequestBody,
  SiteStructureGenerationInfo,
  SiteStructureGenerationRequestBody,
  PromptsOverrideParams,
} from './aiContentProviderTypes';
import { IPageDescription, ISectionBrief, Volume } from '@wix/adi-content-api';
import { getCeTypeByContentCategory } from '../caas/caasUtils';
import { PresetContentCategory } from '../caas/caasConsts';

const capitalizeFirstCharIfNeeded = (input: string): string => {
  if (input.length === 0) {
    return input;
  }

  const firstChar = input.charAt(0);
  if (firstChar === firstChar.toLowerCase()) {
    return firstChar.toUpperCase() + input.slice(1);
  }

  return input;
};

const filterHeaderFooterSectionTypes = (section: AiSectionMetadata) => {
  return (
    section.type !== SectionTypeToFilter.HEADER &&
    section.type !== SectionTypeToFilter.FOOTER
  );
};

const filterLegalPages = (page: AiPageMetadata) => {
  return !legalPagesToFilterSet.has(page.type.toLowerCase());
};

const createPagesSectionLabelTypeListRequest = (
  pagesSectionLabelMap: AiPageMetadata[],
) =>
  pagesSectionLabelMap.map((page) =>
    page.sections.map((section) => section.type),
  );

const filterUnusedData = (pages: AiPageMetadata[]) => {
  return pages.filter(filterLegalPages).map(filterUnusedSectionsData);
};

const filterUnusedSectionsData = (page: AiPageMetadata) => {
  const filterHeaderFooterSections = page.sections.filter(
    filterHeaderFooterSectionTypes,
  );
  return {
    ...page,
    sections: filterHeaderFooterSections,
  };
};

const createSectionLabelsMapPromiseFactory = (
  httpClient: HttpClient,
  sectionsLabelsForMapping: string[][],
  promptsOverride?: PromptsOverrideParams,
) => {
  return sectionsLabelsForMapping.map((sectionLabels: string[]) =>
    getSectionLabels(httpClient, sectionLabels, promptsOverride),
  );
};

const createNewSiteMapWithMappedSectionLabels = (
  siteData: AiPageMetadata[],
  mappedSectionLabelsResults: Record<string, string>[],
) => {
  return siteData.map((page: AiPageMetadata, pageIdx: number) => {
    return {
      ...page,
      sections: page.sections.map((section: AiSectionMetadata) => {
        return {
          ...section,
          type: capitalizeFirstCharIfNeeded(
            mappedSectionLabelsResults[pageIdx][section.type],
          ) as PresetContentCategory,
        };
      }),
    };
  });
};

const getSgSectionLabelTypes = async (
  httpClient: HttpClient,
  siteData: AiPageMetadata[],
  promptsOverride?: PromptsOverrideParams,
) => {
  const sectionsLabelsForMappingRequest =
    createPagesSectionLabelTypeListRequest(siteData);
  const sectionLabelsMapPromiseFactory = createSectionLabelsMapPromiseFactory(
    httpClient,
    sectionsLabelsForMappingRequest,
    promptsOverride,
  );
  const mappedSectionLabelsResults = await Promise.all(
    sectionLabelsMapPromiseFactory,
  );
  return mappedSectionLabelsResults;
};

const createSgSiteDataStructure = (
  siteData: AiPageMetadata[],
): IPageDescription[] => siteData.map(createSgPageDataStructure);

const createSgPageDataStructure = (
  pageData: AiPageMetadata,
): IPageDescription => {
  return {
    name: pageData.title,
    sections: pageData.sections.map(
      createSgSectionDataStructure,
    ) as ISectionBrief[],
    optional: false,
  };
};

const createSgSectionDataStructure = (
  sectionData: AiSectionMetadata,
): Partial<ISectionBrief> => {
  return {
    id: sectionData.title,
    ceType: getCeTypeByContentCategory(
      sectionData.type as PresetContentCategory,
    ),
    volume: Volume.Recommended, // TODO: where to bring volume from?
  };
};

const convertAiSiteDataToSgSiteData = async (
  httpClient: HttpClient,
  siteData: AiPageMetadata[],
  promptsOverride?: PromptsOverrideParams,
) => {
  const siteDataWithFilteredSections = filterUnusedData(siteData);

  const mappedSectionLabelsResults = await getSgSectionLabelTypes(
    httpClient,
    siteDataWithFilteredSections,
    promptsOverride,
  );

  const siteMapWithMappedSectionLabels =
    createNewSiteMapWithMappedSectionLabels(
      siteDataWithFilteredSections,
      mappedSectionLabelsResults,
    );

  return createSgSiteDataStructure(siteMapWithMappedSectionLabels);
};

export const getAllPages = async (
  httpClient: HttpClient,
  siteInfo: SiteStructureGenerationInfo,
): Promise<IPageDescription[]> => {
  const siteData = await fetchFullSiteWithRetry(httpClient, {
    name: siteInfo.name || '',
    description: siteInfo.description || '',
    category: siteInfo.category,
    goals: siteInfo.goals || '',
    pages: [],
    language: siteInfo.language || 'en',
    apps: siteInfo.apps || [],
    ...(siteInfo.promptsOverride && {
      promptsOverride: siteInfo.promptsOverride,
    }),
  });
  const allPagesWithSgSiteData = await convertAiSiteDataToSgSiteData(
    httpClient,
    siteData,
    siteInfo.promptsOverride,
  );
  return allPagesWithSgSiteData;
};

const fetchFullSiteWithRetry = async (
  httpClient: HttpClient,
  properties: SiteStructureGenerationRequestBody,
  count: number = 1,
  maxRetries: number = 3,
) => {
  try {
    const { data } = await httpClient.request({
      method: 'POST',
      url: GENERATE_FULL_SITE_MAP,
      data: properties,
    });
    return data.pages;
  } catch (error) {
    if (count <= maxRetries) {
      return fetchFullSiteWithRetry(httpClient, properties, count + 1);
    }
    throw error;
  }
};

export const getSectionLabels = async (
  httpClient: HttpClient,
  titles: string[],
  promptsOverride?: PromptsOverrideParams,
): Promise<Record<string, string>> => {
  return fetchSectionLabelsWithRetry(httpClient, {
    titles,
    ...(promptsOverride && { promptsOverride }),
  });
};

const fetchSectionLabelsWithRetry = async (
  httpClient: HttpClient,
  properties: SectionMappingRequestBody,
  count: number = 1,
  maxRetries: number = 3,
) => {
  try {
    const { data } = await httpClient.request({
      method: 'POST',
      url: SECTION_LABEL_MAPPING,
      data: properties,
    });
    return data;
  } catch (error) {
    if (count <= maxRetries) {
      return fetchSectionLabelsWithRetry(httpClient, properties, count + 1);
    }
    throw error;
  }
};
